import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { Flame, Trophy, Mic } from "lucide-react";

export default function Feed() {
  const qc = useQueryClient();
  const { data: feed = [], isFetching } = useQuery({
    queryKey: ["feed"],
    queryFn: async () => (await api.get("/api/feed")).data,
    refetchInterval: 20000,
  });

  const cheer = async (item) => {
    // optimistic
    qc.setQueryData(["feed"], (old = []) =>
      old.map((x) => x.id === item.id
        ? { ...x, cheered: !x.cheered, cheers: x.cheers + (x.cheered ? -1 : 1) } : x));
    try { await api.post(`/api/feed/${item.id}/cheer`); }
    catch { toast.error("Couldn't send cheer"); qc.invalidateQueries({ queryKey: ["feed"] }); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.progress} overline="the box · live" title="PR Feed"
        subtitle="Every personal record across the gym, as it happens. Cheer your teammates on." />

      {feed.length === 0 && !isFetching ? (
        <div className="relative overflow-hidden rounded-xl border border-[#1E222A] p-10 md:p-14 text-center">
          <img src={IMG.strength} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at center,rgba(5,5,5,0.35),#050505 80%)" }} />
          <Trophy className="relative w-10 h-10 mx-auto mb-3" style={{ color: "#FFD24A", filter: "drop-shadow(0 0 12px rgba(255,210,74,0.7))" }} />
          <h3 className="relative font-heading font-black text-2xl md:text-3xl tracking-tight">No personal records yet.</h3>
          <p className="relative text-[#B7BECB] mt-2 max-w-md mx-auto">Be the first. Tell your OCTON coach what you lifted and your PR lands here for the whole gym to cheer.</p>
          <a href="/app/coach" className="relative mt-6 inline-flex items-center gap-2 bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-6 font-medium shadow-[0_0_30px_rgba(204,255,0,0.35)]">Log a lift</a>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-3">
          {feed.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className={`surface rounded-lg p-4 flex items-center gap-4 border ${item.is_me ? "border-acid/40" : "border-transparent"}`} data-testid="feed-item">
              <div className="w-11 h-11 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center font-bold shrink-0">
                {item.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className={item.is_me ? "text-acid font-medium" : "font-medium"}>{item.is_me ? "You" : item.name}</span>
                  <span className="text-[#8A91A6]"> hit a PR on </span>
                  <span className="font-medium">{item.movement}</span>
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-heading font-black text-acid">{item.display}</span>
                  {item.source === "voice" && <Mic className="w-3 h-3 text-[#565C6F]" />}
                  <span className="mono-label">{item.date?.slice(0, 10)}</span>
                </div>
              </div>
              <button onClick={() => cheer(item)} data-testid="cheer-btn"
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-9 border transition-colors ${
                  item.cheered ? "border-acid bg-acid/10 text-acid" : "border-[#1E222A] text-[#8A91A6] hover:text-white hover:border-[#343A4A]"}`}>
                <Flame className={`w-4 h-4 ${item.cheered ? "fill-acid" : ""}`} />
                <span className="text-sm font-medium">{item.cheers}</span>
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
